package game.entity

import com.soywiz.korma.geom.PointInt
import game.*
import kotlin.math.abs
import kotlin.math.sign

class MazeGhost(val behaviour: Behaviour, var coolDown: Int) : Enemy(10, 5) {
    enum class Behaviour {
        Chase,
        Ambush,
        Random,
        Shy
    }

    var step = 0
    var action = 0
    var moveCount = 0
    val speed = 12
    var colVec = 0
    var rowVec = 0
    var targetCol = 0
    var targetRow = 0

    // A*
    private val paths = IntArray(ROOM_WIDTH * ROOM_HEIGHT) { 0 }
    private val visited = BooleanArray(ROOM_WIDTH * ROOM_HEIGHT) { false }

    // Cached list of test point to avoid re-alloc each tick
    class PointCost(val col: Int, val row: Int)

    private val testVecs = arrayOf(
        PointCost(-1, 0),
        PointCost(1, 0),
        PointCost(0, -1),
        PointCost(0, 1),
    )

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById("npc-dark-red")
    }

    override fun onTick(game: Game, room: Room) {
        if (coolDown > 0) {
            coolDown--
        } else {
            if (!exitingHome(game, room)) {
                if (moveCount == 0) {
                    when (behaviour) {
                        Behaviour.Chase -> targetChase(game, room)
                        Behaviour.Ambush -> targetAmbush(game, room)
                        Behaviour.Random -> targetRandom(game, room)
                        Behaviour.Shy -> targetShy(game, room)
                    }

                }
                vectorToTarget(game, room)

                col += colVec
                row += rowVec

                if (moveCount > 0) {
                    moveCount--
                }
            }

            coolDown = speed
        }
    }

    private fun targetShy(game: Game, room: Room) {
        if (action == 1) {
            // chase
            targetChase(game, room)
        }

        step--

        if (step <= 0) {
            action = if (action == 1) {
                // Move to corner
                targetRow = if (row in 0..ROOM_HEIGHT / 2) {
                    ROOM_HEIGHT - 2
                } else {
                    1
                }

                targetCol = if (col in 0..ROOM_WIDTH / 2) {
                    ROOM_WIDTH - 2
                } else {
                    1
                }

                0
            } else {
                1
            }

            step = if (action == 1) {
                15
            } else {
                10
            }
        }
    }

    private fun targetRandom(game: Game, room: Room) {
        if (action == 1) {
            // chase
            targetChase(game, room)
        }

        step--

        if (step <= 0) {
            action = if (action == 1) {
                // Move to corner
                targetRow = if (row in 0..ROOM_HEIGHT / 2) {
                    16
                } else {
                    4
                }

                targetCol = if (col in 0..ROOM_WIDTH / 2) {
                    8
                } else {
                    42
                }

                0
            } else {
                1
            }

            step = if (action == 1) {
                15
            } else {
                10
            }
        }
    }

    fun sign(value: Int) = if (value > 0) 1 else if (value < 0) -1 else 0

    private fun targetAmbush(game: Game, room: Room) {
        val playerColVel = game.player.colVel
        val playerRowVel = game.player.rowVel

        if (playerColVel == 0 && playerRowVel == 0) {
            targetChase(game, room)
        } else if (abs(game.player.col - col) > 10 && abs(game.player.row - row) > 10) {
            targetChase(game, room)
        } else {

            var switchDirection = false

            targetCol = game.player.col
            targetRow = game.player.row

            var testCol = col
            var testRow = row

            while (true) {
                if (room[testCol + playerColVel, testRow + playerRowVel].type == TileType.Floor &&
                    room[targetCol + playerColVel, targetRow + playerRowVel].type == TileType.Floor
                ) {
                    testCol += playerColVel
                    testRow += playerRowVel
                    targetCol += playerColVel
                    targetRow += playerRowVel
                    moveCount++
                } else {
                    break
                }
            }
        }

//        println("moveCount: $moveCount, col: $col, row: $row, targetCol: $targetCol, targetRow: $targetRow, colVel: $colVec, rowVel: $rowVec")
    }

    private fun vectorToTarget(game: Game, room: Room) {
        for (i in 0 until paths.size) {
            paths[i] = 0
            visited[i] = false
        }

        calcPathCost(0, targetCol, targetRow, room)

        var bestCost = 1000

        for (pointCost in testVecs) {
            if (room[col + pointCost.col, row + pointCost.row].type == TileType.Floor) {
                val testCost = getCostAt(col + pointCost.col, row + pointCost.row)

                if (bestCost > testCost) {
                    colVec = pointCost.col
                    rowVec = pointCost.row
                    bestCost = testCost
                }
            }
        }
    }

    private fun calcPathCost(cost: Int, currCol: Int, currRow: Int, room: Room) {
        if (room[currCol, currRow].type == TileType.Wall) {
            return
        } else {
            val visited = getVisitedAt(currCol, currRow)
            val testCost = getCostAt(currCol, currRow)

            if (!visited || cost < testCost) {
                setCostAt(currCol, currRow, cost)
                setVisitedAt(currCol, currRow, true)

                calcPathCost(cost + 1, currCol + 1, currRow, room)
                calcPathCost(cost + 1, currCol - 1, currRow, room)
                calcPathCost(cost + 1, currCol, currRow + 1, room)
                calcPathCost(cost + 1, currCol, currRow - 1, room)
            }
        }
    }

    private fun setVisitedAt(currCol: Int, currRow: Int, value: Boolean) {
        visited[currRow * ROOM_WIDTH + currCol] = value
    }

    private fun getVisitedAt(currCol: Int, currRow: Int) = visited[currRow * ROOM_WIDTH + currCol]

    private fun setCostAt(currCol: Int, currRow: Int, cost: Int) {
        paths[currRow * ROOM_WIDTH + currCol] = cost
    }

    private fun getCostAt(currCol: Int, currRow: Int) = paths[currRow * ROOM_WIDTH + currCol]

    private fun targetChase(game: Game, room: Room) {
        targetCol = game.player.col
        targetRow = game.player.row
    }

    private fun exitingHome(game: Game, room: Room): Boolean {
        if (room[col, row].type == TileType.Wall) {
            if (col < 25 || col > 25) {
                col = 25
            } else if (row in 8..10) {
                row--
            }

            return true
        } else {
            return false
        }
    }
}

class MazeShard : Entity() {
    var onPickup: () -> Unit = {}

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById("small-shard")
    }

    override fun onTick(game: Game, room: Room) {
        if (game.player.col == col && game.player.row == row) {
            onPickup.invoke()
            room.removeEntity(this)
        }
    }
}

class MazeBattle(val onDone: (MazeBattle) -> Unit = {}) : Entity() {
    var shardCount = 0
    var pickupCount = 0

    val ghost1 = MazeGhost(MazeGhost.Behaviour.Chase, 10).also {
        it.col = 25
        it.row = 7
    }
    val ghost2 = MazeGhost(MazeGhost.Behaviour.Ambush, 110).also {
        it.col = 25
        it.row = 10
    }
    val ghost3 = MazeGhost(MazeGhost.Behaviour.Shy, 210).also {
        it.col = 24
        it.row = 10
    }
    val ghost4 = MazeGhost(MazeGhost.Behaviour.Random, 310).also {
        it.col = 26
        it.row = 10
    }

    override fun onInit(game: Game, room: Room) {
        val illegalPositions = arrayOf(
            PointInt(25, 9),
            PointInt(24, 10),
            PointInt(25, 10),
            PointInt(26, 10),
            PointInt(25, 13),
            PointInt(25, 13),
            // Troll cactus:
            PointInt(1, 4),
            PointInt(1, 16),
            PointInt(49, 4),
            PointInt(49, 16),
        )

        for (row in 0 until ROOM_HEIGHT) {
            for (col in 1 until ROOM_WIDTH step 2) {
                if (room[col, row].type != TileType.Wall
                    && illegalPositions.none { col == it.x && row == it.y }
                    && room.getEntityAt(col, row) == null
                ) {
                    room.addEntity(MazeShard().also {
                        it.col = col
                        it.row = row
                        it.onPickup = {
                            shardCount--
                            game.playSound("shard-pickup-1")
                        }
                    })

                    shardCount++
                }
            }
        }

        room.addEntity(ghost1)
        room.addEntity(ghost2)
        room.addEntity(ghost3)
        room.addEntity(ghost4)
    }

    override fun onTick(game: Game, room: Room) {
        if (shardCount == 0) {
            game.startTeleportTransition(
                game.player.col,
                game.player.row,
                27,
                10,
                "rink-battle",
                true
            )

            onDone.invoke(this)
        }
    }

    override fun onRemove(game: Game, room: Room) {
        room.removeEntity(ghost1)
        room.removeEntity(ghost2)
        room.removeEntity(ghost3)
        room.removeEntity(ghost4)
    }
}